import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Handshake, CheckCircle2, ArrowRight, Users, ShieldCheck, TrendingUp } from "lucide-react";

const AVAILABILITY = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "both", label: "Both" },
];
const CONTACT_PREFERENCE = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone call" },
  { value: "either", label: "Either" },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Partners() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    sector: "",
    sector_other: "",
    availability: "both",
    contact_preference: "either",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const isOther = form.sector === "other";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.sector) {
      toast.error("Please complete all required fields");
      return;
    }
    if (isOther && !form.sector_other.trim()) {
      toast.error("Please tell us your sector");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partner-applications", form);
      setDone(true);
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partners-page" className="bg-[#F5EFE4]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url('${HERO_IMG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-[#1A1815]/50" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="overline">Interested in Joining?</p>
          <h1 className="font-serif text-5xl lg:text-7xl mt-4 leading-[1.02] text-[#F5EFE4] max-w-3xl">
            Partner with <em className="italic text-[#C5A059]">Luxor.</em>
          </h1>
          <div className="mt-4"><span className="divider-gold-center" /></div>
          <p className="mt-8 max-w-xl text-[#F5EFE4]/70 leading-relaxed">
            We work with a small, trusted circle of specialists across every discipline. If you
            operate to a quiet, exceptional standard, we would like to hear from you.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            {[
              [Users, "Vetted client base"],
              [ShieldCheck, "Predictable, quality bookings"],
              [TrendingUp, "Grow with the house"],
            ].map(([I, t]) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059]"><I size={14} /></div>
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#F5EFE4]/90">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20">
        {done ? (
          <div className="bg-white border border-[#C5A059] p-12 text-center" data-testid="partners-success">
            <div className="w-14 h-14 rounded-full bg-[#C5A059] text-white flex items-center justify-center mx-auto"><CheckCircle2 size={26} /></div>
            <p className="overline mt-6">Received</p>
            <h2 className="font-serif text-4xl mt-3 text-[#1A1815]">Thank you.</h2>
            <p className="mt-4 text-[#6B665E] max-w-lg mx-auto">
              Your application has been received. Our partnerships team will review and be in touch
              via your preferred channel within 3 business days.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="w-12 h-12 rounded-full bg-[#C5A059] text-white flex items-center justify-center"><Handshake size={20} /></div>
              <h2 className="font-serif text-3xl lg:text-4xl mt-6 text-[#1A1815]">
                Tell us about you.
              </h2>
              <p className="mt-4 text-[#6B665E] text-sm leading-relaxed">
                Fill in the form and we will match your service area to current openings. All
                enquiries are handled in confidence.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-[#1A1815]">
                <li className="flex gap-3"><CheckCircle2 size={15} className="text-[#C5A059] mt-0.5" /> Response within 3 business days</li>
                <li className="flex gap-3"><CheckCircle2 size={15} className="text-[#C5A059] mt-0.5" /> Confidential review</li>
                <li className="flex gap-3"><CheckCircle2 size={15} className="text-[#C5A059] mt-0.5" /> On-brand onboarding</li>
              </ul>
            </div>

            <form onSubmit={submit} className="lg:col-span-8 bg-white border border-[#E4D9C1] p-8" data-testid="partner-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="luxor-label">Full Name *</label>
                  <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="luxor-field" data-testid="partner-full-name" required />
                </div>
                <div>
                  <label className="luxor-label">Email Address *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="luxor-field" data-testid="partner-email" required />
                </div>
                <div>
                  <label className="luxor-label">Phone Number *</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="luxor-field" data-testid="partner-phone" required />
                </div>
                <div>
                  <label className="luxor-label">Sector *</label>
                  <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="luxor-field" data-testid="partner-sector" required>
                    <option value="">Select a sector</option>
                    {services.map((s) => (
                      <option key={s.slug} value={s.slug}>{s.name}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>
                {isOther && (
                  <div className="md:col-span-2">
                    <label className="luxor-label">Please specify *</label>
                    <input value={form.sector_other} onChange={(e) => setForm({ ...form, sector_other: e.target.value })} className="luxor-field" data-testid="partner-sector-other" required />
                  </div>
                )}
                <div>
                  <label className="luxor-label">Availability to be contacted</label>
                  <div className="flex flex-wrap gap-3 mt-2" data-testid="partner-availability">
                    {AVAILABILITY.map((o) => (
                      <label key={o.value} className={`px-4 py-2 border cursor-pointer transition-colors text-sm ${form.availability === o.value ? "border-[#C5A059] bg-[#C5A059]/10 text-[#8B6D2E]" : "border-[#E4D9C1] text-[#1A1815] hover:border-[#C5A059]/50"}`}>
                        <input type="radio" name="availability" value={o.value} checked={form.availability === o.value} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="hidden" />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="luxor-label">Contact preference</label>
                  <div className="flex flex-wrap gap-3 mt-2" data-testid="partner-contact-pref">
                    {CONTACT_PREFERENCE.map((o) => (
                      <label key={o.value} className={`px-4 py-2 border cursor-pointer transition-colors text-sm ${form.contact_preference === o.value ? "border-[#C5A059] bg-[#C5A059]/10 text-[#8B6D2E]" : "border-[#E4D9C1] text-[#1A1815] hover:border-[#C5A059]/50"}`}>
                        <input type="radio" name="pref" value={o.value} checked={form.contact_preference === o.value} onChange={(e) => setForm({ ...form, contact_preference: e.target.value })} className="hidden" />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="luxor-label">A short note (optional)</label>
                  <textarea rows={4} maxLength={600} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="luxor-field resize-none" placeholder="Company, experience, and anything else we should know" data-testid="partner-message" />
                  <div className="text-right text-xs text-[#6B665E] mt-1">{form.message.length}/600</div>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="luxor-btn-primary mt-8 w-full justify-center disabled:opacity-50" data-testid="partner-submit">
                {submitting ? "Sending…" : (<>Submit Application <ArrowRight size={14} /></>)}
              </button>
              <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[#6B665E] mt-3">
                All applications handled in confidence
              </p>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
